// @file Vuex Store module for Post Actions menu (menu using the hamburger button on corner of the post)

/* eslint-disable no-param-reassign */
import { useSurfacePostActionStore } from '@@/pinia/surface_post_action'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import type { Cid, Post } from '@@/types'
import type { PostActionState, RootState } from '@@/vuexstore/surface/types'
import type { Module } from 'vuex'

const postActionModule: Module<PostActionState, RootState> = {
  namespaced: true,
  state: {
    expandedPostCid: null,
    xExpandedPostTextPanel: false,
    shouldFocusExpandedPostAddCommentInput: false,
  },
  getters: {
    expandedPostCid: (state) => state.expandedPostCid,
    expandedPost: (state, getters, rootState, rootGetters): Post | undefined => {
      return state.expandedPostCid == null ? undefined : rootGetters['post/postEntitiesByCid'][state.expandedPostCid]
    },
    xExpandedPostTextPanel: (state) => state.xExpandedPostTextPanel,
    shouldFocusExpandedPostAddCommentInput: (state) => state.shouldFocusExpandedPostAddCommentInput,
    isExpandedPostPreviewPost: (state, getters, rootState, rootGetters): boolean => {
      return getters.expandedPost && getters.expandedPost.id === rootGetters.coverPostId
    },
    expandedPostServerId: (state, getters, rootState) => {
      return state.expandedPostCid && rootState.post.postEntitiesByCid[state.expandedPostCid]
        ? rootState.post.postEntitiesByCid[state.expandedPostCid].id
        : null
    },
    expandedPostFrameUrl: (state, getters, rootState, rootGetters) => {
      if (!rootGetters.publicKey || !state.expandedPostCid) {
        return 'about:blank'
      }
      return `/padlets/${rootGetters.publicKey}/frames/post${getters.expandedPostServerId}`
    },
    expandedPostMobileFrameUrl: (state, getters, rootState, rootGetters) => {
      if (!rootGetters.publicKey || !state.expandedPostCid) {
        return 'about:blank'
      }
      return `/padlets/${rootGetters.publicKey}/frames/post_v2/${getters.expandedPostServerId}`
    },
    expandedPostMobileBodyFrameUrl: (state, getters, rootState, rootGetters) => {
      if (!rootGetters.publicKey || !state.expandedPostCid) {
        return 'about:blank'
      }
      return `/padlets/${rootGetters.publicKey}/frames/body${getters.expandedPostServerId}`
    },
    expandedPostPostsToNavigate: (state, _getters, _rootState, rootGetters): Post[] => {
      const surfacePostsStore = useSurfacePostsStore()
      // If surface is sectioned, get sorted + filtered posts grouped by sections. Flatten them.
      if (rootGetters.isSectioned as boolean) {
        return surfacePostsStore.postsBySortedGroups.flat()
      }
      // Else just get sorted + filtered posts
      return surfacePostsStore.currentSortedPosts
    },
    expandedPostPreviousPostCid: (_state, getters): Cid | null => {
      const currentPostIndex = getters.expandedPostPostsToNavigate.findIndex(
        (p: Post) => p.cid === getters.expandedPostCid,
      )
      const previousPostIndex = currentPostIndex - 1
      const previousPost = getters.expandedPostPostsToNavigate[previousPostIndex]
      return previousPost?.cid ?? null
    },
    expandedPostNextPostCid: (_state, getters): Cid | null => {
      const currentPostIndex = getters.expandedPostPostsToNavigate.findIndex(
        (p: Post) => p.cid === getters.expandedPostCid,
      )
      const nextPostIndex = currentPostIndex + 1
      const nextPost = getters.expandedPostPostsToNavigate[nextPostIndex]
      return nextPost?.cid ?? null
    },
  },
  mutations: {
    EXPAND_POST(state, postCid): void {
      state.expandedPostCid = postCid
    },
    UNEXPAND_POST(state): void {
      state.expandedPostCid = null
    },
    TOGGLE_EXPANDED_POST_TEXT_PANEL(state, status): void {
      if (status !== undefined) {
        state.xExpandedPostTextPanel = status
      } else {
        state.xExpandedPostTextPanel = !state.xExpandedPostTextPanel
      }
    },
    SET_SHOULD_FOCUS_EXPANDED_POST_ADD_COMMENT_INPUT(state): void {
      state.shouldFocusExpandedPostAddCommentInput = true
    },
    UNSET_SHOULD_FOCUS_EXPANDED_POST_ADD_COMMENT_INPUT(state): void {
      state.shouldFocusExpandedPostAddCommentInput = false
    },
  },
  actions: {
    /* ------------
     * SURFACE STATE MODIFICATION OPERATIONS
     * ------------ */
    expandPost(
      { commit, state },
      { postCid, xTextPanel, shouldFocusInput }: { postCid: Cid; xTextPanel?: boolean; shouldFocusInput?: boolean },
    ): void {
      useSurfacePostActionStore().unsetCurrentlyPlayingPost()
      commit('EXPAND_POST', postCid)
      if (xTextPanel !== undefined) {
        commit('TOGGLE_EXPANDED_POST_TEXT_PANEL', xTextPanel)
      }
      // Only focus input if text panel is also open
      if (xTextPanel && shouldFocusInput) {
        commit('SET_SHOULD_FOCUS_EXPANDED_POST_ADD_COMMENT_INPUT')
      }
    },
    unexpandPost({ commit, state }) {
      commit('UNEXPAND_POST')
      useSurfacePostActionStore().unsetCurrentlyPlayingPost()
      commit('UNSET_SHOULD_FOCUS_EXPANDED_POST_ADD_COMMENT_INPUT')
    },
    toggleExpandedPostTextPanel({ commit }, payload?: { status: boolean }): void {
      let status: boolean | undefined
      if (payload != undefined && payload.status !== undefined) status = payload.status
      commit('TOGGLE_EXPANDED_POST_TEXT_PANEL', status)
    },
  },
}

export default postActionModule
